import React, { memo, useMemo, useContext, useState, useEffect, useCallback } from 'react';
import { Skeleton } from '@material-ui/lab';
import { PatientChartContext } from '../../../contexts/patientChart';
import { APPROVAL_VALIDATION_ERROR, CONFIRMATION_POPUP_KEY, MODAL_KEY } from '../../../constants/patientChart.constant';
import PPButton from '../../subComponents/button';

/**
 * @description The expression is copied from version 1 snippet
 * @param {string} url 
 * @returns {boolean}
 */
const isValidURLV1 = (url) => {
  const expression = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
  const regex = new RegExp(expression);
  return regex.test(url);
};

const DoseSpotLoader = () => (
  <div className='flex flex-column gap-28 flex-fill border-2-dark-grey'>
    <div className='flex flex-column'>
      <div className='flex'>
        <Skeleton variant='rect' className='flex flex-fill height-56-i' />
      </div>

      <div className='flex height-56 border-top-2-dark-grey border-bottom-2-dark-grey bg-white p-4'>
        <Skeleton variant='text' className='width-140' />
      </div>
    </div>

    <div className='flex justify-content-space-evenly'>
      <div className='width-480 height-240 border-2-lighter-grey bg-white p-4'>
        <Skeleton variant='text' className='width-320' />
      </div>
      <div className='flex flex-column width-480 height-240 border-2-lighter-grey bg-white p-4'>
        <Skeleton variant='text' className='width-320' />
        <Skeleton variant='text' className='width-420' />
      </div>
    </div>

    <div className='flex justify-content-center'>
      <Skeleton variant='rect' className='width-280 height-42-i rounded-lg' />
    </div>

    <div className='flex justify-content-center'>
      <Skeleton variant='rect' className='width-280 height-42-i rounded-lg' />
    </div>
  </div>
);

/**
 * @param {{
 *  url: string;
 *  productId: number;
 *  orderId: number | string;
 * }} param 
 * @returns {React.JSX.Element}
 */
const DoseSpotIframe = ({
  productId,
  orderId,
}) => {
  const [iframeElement, setIframeElement] = useState({
    url: '',
    visible: false,
  });
  const {
    fetchJumpStartURL,
  } = useContext(PatientChartContext);

  const fetchAsync = useCallback(async () => {
    const { status, data } = await fetchJumpStartURL({
      ProductId: productId,
      OrderId: orderId,
    });

    if (status !== 200) {
      return {};
    }

    if (!data?.Data?.StringValue?.length) {
      return {};
    }

    setIframeElement({
      url: data.Data.StringValue,
      visible: true,
    });
  }, [orderId, productId]);

  useEffect(fetchAsync, []);

  if (!iframeElement.visible) {
    return (
      <DoseSpotLoader />
    );
  }

  return (
    <iframe title='dose-spot-jump-start-window' src={iframeElement.url} className='flex flex-fill' />
  );
}

const IFrameCollection = new Map([
  [true, DoseSpotIframe],
  [false, DoseSpotLoader],
]);

/**
 * @param {{
 *  url: string;
 *  productId: number;
 *  orderId: number | string;
 * }} param 
 * @returns {React.JSX.Element}
 */
const DoseSpotUI = ({
  url = '',
  productId,
  orderId,
}) => {
  const isValid = useMemo(() => isValidURLV1(url), [url]);

  const IFrame = useMemo(() => IFrameCollection.get(isValid), [isValid]);

  const props = useMemo(() => {
    const Collection = new Map([
      [true, { url, productId, orderId }],
      [false, {}],
    ]);
    return Collection.get(isValid);
  }, [url, productId, orderId, isValid]);

  return (
    <div className='flex flex-column flex-fill flex-gap-3 m-b-5'>
      <div className='flex align-items-center color-blue-v1 font-20 bold'>
        Please Complete the Following Steps to Approve and Send a New Prescription:
      </div>
  
      <div className='flex align-items-center color-orange-v3 font-20 bold'>
        1. Add a Prescription
      </div>
  
      <div className='flex align-items-center color-orange-v3 font-20 bold'>
        2. Confirm the patient's pharmacy of choice
      </div>
  
      <div className='flex align-items-center color-orange-v3 font-20 bold'>
        3. Select the "Approve and Send" in Pending Medications before exiting
      </div>
  
      <IFrame {...props} />
    </div>
  );
};

export const MemoizedDoseSpotUI = memo(DoseSpotUI);

export const DoseSpotActions = ({
  validationError = APPROVAL_VALIDATION_ERROR(),
  cancelButtonLabel = 'Cancel',

  onApprove: handleApprove = async () => {},
  onCancel: handleCancel = async () => {},
}) => {
  const {
    loading,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);

  const _disabled = useMemo(() => validationError.size > 0, [validationError]);
  const _loading = useMemo(() => loading.has(MODAL_KEY.APPROVE), [loading]);

  const handleCancelActions = async ({ keys }) => {
    handleMultipleModalClose({ keys });
    handleCancel();
  };

  return (
    <div className='flex align-items-center justify-content-space-between'>
      <div className='approval-pin flex align-items-center width-180' />
  
      <div className='flex flex-column width-390 justify-content-center'>
        <PPButton
          label='Complete and Exit'
          variant='contained'
          loading={_loading}
          disabled={_disabled}
          onClick={handleApprove.bind(this, {
            key: CONFIRMATION_POPUP_KEY.APPROVE,
            modal: {},
          })}
        />
      </div>
  
      <div className='flex align-items-center justify-content-flex-end width-180'>
        <PPButton
          label={cancelButtonLabel}
          variant='contained'
          disabled={_loading}
          onClick={handleCancelActions.bind(this, {
            keys: [
              MODAL_KEY.APPROVE,
              MODAL_KEY.DOSESPOT_JUMPSTART,
            ],
          })}
        />
      </div>
    </div>
  );
};

export default MemoizedDoseSpotUI;
